import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Eye, X, ChevronUp } from 'lucide-react'
import api from '../services/api'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken, clearAuth } from '../utils/userData'

const FORMAT_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-Person' },
  { value: 'hybrid', label: 'Hybrid' },
]

const STATUS_STYLES = {
  new: 'bg-yellow-100 text-yellow-800',
  contacted: 'bg-blue-100 text-blue-800',
  closed: 'bg-green-100 text-green-800',
}

const INITIAL_FORM = {
  title: '',
  schedule: '',
  timeSlot: '',
  level: '',
  fee: '',
  format: 'online',
  spotsText: '',
  active: true,
}

const FacilitatorClassManage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [listings, setListings] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(INITIAL_FORM)
  const [saving, setSaving] = useState(false)
  const [expandedInterests, setExpandedInterests] = useState({})
  const [interests, setInterests] = useState({})
  const [loadingInterests, setLoadingInterests] = useState({})

  useEffect(() => {
    const token = getToken()
    if (!token) {
      navigate('/')
      return
    }
    fetchListings()
  }, [navigate])

  const fetchListings = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/facilitators/classes')
      if (response.data.success) {
        setListings(response.data.data)
      }
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuth()
        navigate('/')
      }
      console.error('Error fetching classes:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchInterests = async (listingId) => {
    try {
      setLoadingInterests(prev => ({ ...prev, [listingId]: true }))
      const response = await api.get(`/api/facilitators/classes/${listingId}/interests`)
      if (response.data.success) {
        setInterests(prev => ({ ...prev, [listingId]: response.data.data }))
      }
    } catch (err) {
      console.error('Error fetching interests:', err)
    } finally {
      setLoadingInterests(prev => ({ ...prev, [listingId]: false }))
    }
  }

  const toggleInterests = (listingId) => {
    const isExpanding = !expandedInterests[listingId]
    setExpandedInterests(prev => ({ ...prev, [listingId]: isExpanding }))
    if (isExpanding && !interests[listingId]) {
      fetchInterests(listingId)
    }
  }

  const openCreateForm = () => {
    setForm(INITIAL_FORM)
    setEditingId(null)
    setShowForm(true)
  }

  const openEditForm = (listing) => {
    setForm({
      title: listing.title || '',
      schedule: listing.schedule || '',
      timeSlot: listing.timeSlot || '',
      level: listing.level || '',
      fee: listing.fee || '',
      format: listing.format || 'online',
      spotsText: listing.spotsText || '',
      active: listing.active ?? true,
    })
    setEditingId(listing.id)
    setShowForm(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) return
    try {
      setSaving(true)
      if (editingId) {
        await api.put(`/api/facilitators/classes/${editingId}`, form)
      } else {
        await api.post('/api/facilitators/classes', form)
      }
      setShowForm(false)
      fetchListings()
    } catch (err) {
      console.error('Error saving class:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (listing) => {
    try {
      await api.put(`/api/facilitators/classes/${listing.id}`, { active: !listing.active })
      fetchListings()
    } catch (err) {
      console.error('Error toggling active:', err)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this class? This cannot be undone.')) return
    try {
      await api.delete(`/api/facilitators/classes/${id}`)
      fetchListings()
    } catch (err) {
      console.error('Error deleting class:', err)
    }
  }

  const handleInterestStatus = async (interestId, status, listingId) => {
    try {
      await api.patch(`/api/facilitators/classes/interests/${interestId}/status`, { status })
      fetchInterests(listingId)
    } catch (err) {
      console.error('Error updating interest status:', err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">My Classes</h1>
              <p className="text-gray-600">Create and manage your class offerings.</p>
            </div>
            <button
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Class
            </button>
          </div>
        </motion.div>

        {listings.length === 0 ? (
          <Card className="text-center py-16">
            <p className="text-gray-500 text-lg mb-4">You haven&apos;t created any classes yet.</p>
            <button
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Your First Class
            </button>
          </Card>
        ) : (
          <div className="space-y-4">
            {listings.map((listing) => (
              <motion.div key={listing.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="overflow-hidden">
                  <div className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold text-gray-900">{listing.title}</h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${listing.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {listing.active ? 'Active' : 'Inactive'}
                          </span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            listing.format === 'online' ? 'bg-blue-100 text-blue-700' :
                            listing.format === 'in-person' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {listing.format}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          {listing.schedule && <span>📅 {listing.schedule}</span>}
                          {listing.timeSlot && <span>⏰ {listing.timeSlot}</span>}
                          {listing.level && <span>📊 {listing.level}</span>}
                          {listing.fee && <span>💰 {listing.fee}</span>}
                          {listing.spotsText && <span>👥 {listing.spotsText}</span>}
                          <span>Interests: {listing._count?.interests || 0}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => toggleInterests(listing.id)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="View interests"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditForm(listing)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(listing)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            listing.active
                              ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                              : 'text-green-700 bg-green-100 hover:bg-green-200'
                          }`}
                        >
                          {listing.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(listing.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <AnimatePresence>
                      {expandedInterests[listing.id] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900 text-sm">Interest Submissions</h4>
                              <button onClick={() => toggleInterests(listing.id)} className="text-gray-400 hover:text-gray-600">
                                <ChevronUp className="w-4 h-4" />
                              </button>
                            </div>
                            {loadingInterests[listing.id] ? (
                              <div className="flex justify-center py-4"><LoadingSpinner /></div>
                            ) : !interests[listing.id]?.length ? (
                              <p className="text-gray-400 text-sm py-4 text-center">No interests yet</p>
                            ) : (
                              <div className="space-y-3">
                                {interests[listing.id].map((interest) => (
                                  <div key={interest.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900 text-sm">{interest.name}</p>
                                      <p className="text-gray-500 text-xs">{interest.email}{interest.phone ? ` · ${interest.phone}` : ''}</p>
                                      {interest.message && (
                                        <p className="text-gray-600 text-xs mt-1 italic">&ldquo;{interest.message}&rdquo;</p>
                                      )}
                                      <p className="text-gray-400 text-xs mt-1">
                                        {new Date(interest.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      </p>
                                    </div>
                                    <select
                                      value={interest.status}
                                      onChange={(e) => handleInterestStatus(interest.id, e.target.value, listing.id)}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border-0 cursor-pointer ${STATUS_STYLES[interest.status] || 'bg-gray-100 text-gray-700'}`}
                                    >
                                      <option value="new">New</option>
                                      <option value="contacted">Contacted</option>
                                      <option value="closed">Closed</option>
                                    </select>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
              onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false) }}
            >
              <motion.div
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '100%', opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto"
              >
                <form onSubmit={handleSave} className="p-6 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingId ? 'Edit Class' : 'New Class'}
                    </h2>
                    <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-full">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={form.title}
                      onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Morning Yoga Flow"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                      <input
                        type="text"
                        value={form.schedule}
                        onChange={(e) => setForm(prev => ({ ...prev, schedule: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., Every Mon & Wed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Time Slot</label>
                      <input
                        type="text"
                        value={form.timeSlot}
                        onChange={(e) => setForm(prev => ({ ...prev, timeSlot: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., 7:00 – 8:00 AM"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                      <input
                        type="text"
                        value={form.level}
                        onChange={(e) => setForm(prev => ({ ...prev, level: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., All levels"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fee</label>
                      <input
                        type="text"
                        value={form.fee}
                        onChange={(e) => setForm(prev => ({ ...prev, fee: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., Rs 500 / session"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                      <select
                        value={form.format}
                        onChange={(e) => setForm(prev => ({ ...prev, format: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {FORMAT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spots</label>
                      <input
                        type="text"
                        value={form.spotsText}
                        onChange={(e) => setForm(prev => ({ ...prev, spotsText: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., 10 spots left"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, active: !prev.active }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.active ? 'bg-primary-600' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-sm font-medium text-gray-700">{form.active ? 'Active' : 'Inactive'}</span>
                  </div>

                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingId ? 'Update Class' : 'Create Class'}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default FacilitatorClassManage
